import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import AvatarViewer from "@/components/AvatarViewer";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import type { LipSyncMorphTarget } from "@/hooks/useLipSync";
import { hasLiveMouth, LiveMouthDriver } from "@/lib/liveMouthDriver";
import type { MirrorPresenceState } from "@/components/mirror/MirrorPresenceConfig";

// ─────────────────────────────────────────────────────────────────────────────
// MirrorLiveAvatar — the Espelho "Live" (Option C) avatar: BUST framing,
// full-screen presence, same lip-sync wiring as the Mirror.
//
// FROZEN-SAFE: consumes the frozen `AvatarViewer` purely through its public
// props (it already accepts framing="bust" + scale/camera/lookAt overrides AND
// audioElement/alignment together — the exact combo BrainMiniMirror ships in
// prod). Lip-sync is byte-identical to MirrorAvatar: audioElement + alignment
// flow through AvatarViewer → AvatarCore → useLipSync (so it inherits the
// viseme fix). The presence halo markup is RECREATED here (not imported from
// the frozen MirrorAvatar). No frozen file is edited.
//
// The /mirror chat keeps MirrorAvatar (full-body); this wrapper renders only on
// /mirror/live.
// ─────────────────────────────────────────────────────────────────────────────

// Bust framing for the full-screen portrait. The Live screen is taller than the
// mini-mirror card, so these start from the mini-mirror values and are meant to
// be tuned by eye on device. Named + isolated for that reason.
export const MIRROR_LIVE_CONFIG = {
  scale: 1.6,
  cameraDistance: 1.7,
  // The Live viewport is a FULL-SCREEN PORTRAIT — taller than the Hero/mini-mirror
  // card — so the camera must aim higher than the card's 2.5 to pull the face into
  // frame (chest-up). Bump up further if the face still sits high; lower if cut at
  // the chin. (Distance is fine; the aim is the lever.)
  lookAtY: 2.5,
} as const;

interface MirrorLiveAvatarProps {
  audioElement?: HTMLAudioElement | null;
  alignment?: ElevenLabsAlignment | null;
  // Estado do Presence Engine + envelope do mic, repassados pelo MirrorChat
  // para a energia (fallback sem avatar). Opcionais: sem eles a energia cai
  // no par isSpeaking/idle derivado do próprio audioElement.
  presenceState?: MirrorPresenceState;
  micLevel?: number;
  // LOTE 3 — LIVE por OpenAI Realtime. Não há <audio> de TTS nem alignment:
  // o que existe é o MediaStream do microfone (energia no listening) e o
  // MediaStream REMOTO da chamada (energia no speaking, via
  // MediaStreamAudioSourceNode — o único caminho que move a linha de
  // energia com voz real; createMediaStreamSource não reroteia a saída,
  // então é seguro no iOS). analyserNode alimenta a boca: pelo driver
  // próprio deste wrapper quando o AvatarCore entrega o mesh (LOTE 4.1),
  // senão pelo caminho legado de amplitude do AvatarCore.
  micStream?: MediaStream | null;
  remoteStream?: MediaStream | null;
  analyserNode?: AnalyserNode | null;
}

export default function MirrorLiveAvatar({
  audioElement,
  alignment,
  presenceState,
  micLevel,
  micStream,
  remoteStream,
  analyserNode,
}: MirrorLiveAvatarProps) {
  const { avatarUrl, hasAvatar, loading } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície — era o bug do LIVE: decidia por hasAvatar sozinho e o avatar
  // voltava para quem escolheu energia. Predicado canônico: quem escolheu
  // avatar sem ter GLB próprio também cai na energia (nunca placeholder).
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;

  // Speaking state for the halo pulse + energy fallback — derived from the same
  // audio element that drives lip-sync.
  const [isSpeaking, setIsSpeaking] = useState(false);
  useEffect(() => {
    if (!audioElement) {
      setIsSpeaking(false);
      return;
    }
    const onPlay = () => setIsSpeaking(true);
    const onStop = () => setIsSpeaking(false);
    audioElement.addEventListener("play", onPlay);
    audioElement.addEventListener("pause", onStop);
    audioElement.addEventListener("ended", onStop);
    setIsSpeaking(!audioElement.paused && !audioElement.ended);
    return () => {
      audioElement.removeEventListener("play", onPlay);
      audioElement.removeEventListener("pause", onStop);
      audioElement.removeEventListener("ended", onStop);
    };
  }, [audioElement]);

  // Estado efetivo da energia: o Presence Engine do MirrorChat quando
  // repassado; senão o par speaking/idle derivado do audioElement local.
  const energyState: MirrorPresenceState =
    presenceState ?? (isSpeaking ? "speaking" : "idle");

  // ── A BOCA DO LIVE (LOTE 4.1, 05/09/2026) ──────────────────────────────────
  // O AvatarCore entrega o mesh ARKit52 pelo prop onLipSyncTargetReady (a
  // única mudança autorizada nos congelados). Com o alvo E o analyser da voz
  // remota, o driver (src/lib/liveMouthDriver.ts) roda um RAF próprio
  // distribuindo a amplitude por jawOpen + mouthFunnel + mouthPucker, com
  // ataque rápido e decaimento lento, SÓ enquanto o estado é "speaking".
  //
  // Enquanto o driver está ativo, o analyser NÃO vai ao AvatarCore: o
  // caminho legado de amplitude dele escreveria jawOpen (ou o osso do
  // maxilar) a partir do mesmo sinal, e dois escritores no mesmo morph é a
  // corrida invisível. Com analyserNode nulo, o legado só relaxa o que ele
  // próprio controla para zero (15 %/frame); no mesh ARKit sem osso de
  // maxilar isso pode atenuar o jawOpen deste driver em até 15 % — aceito e
  // invisível, o teto é 0,38. Sem alvo, OU com um alvo sem nenhum dos três
  // morphs (review Codex P2 do LOTE 4.1: o MetaPerson nativo entrega o mesh
  // sem jawOpen/mouthFunnel/mouthPucker), o analyser volta ao legado: um
  // morph mexendo é melhor que boca parada. driverActive é decidido pela
  // MESMA pergunta que o driver faz (hasLiveMouth), antes de render — decidir
  // só pela presença do alvo deixava esse mesh sem driver E sem analyser.
  const [lipTarget, setLipTarget] = useState<LipSyncMorphTarget | null>(null);
  const onLipSyncTargetReady = useCallback((t: LipSyncMorphTarget | null) => setLipTarget(t), []);
  const driverRef = useRef<LiveMouthDriver | null>(null);
  const driverActive = !!lipTarget && !!analyserNode && hasLiveMouth(lipTarget);
  useEffect(() => {
    if (!lipTarget || !analyserNode) return;
    if (!hasLiveMouth(lipTarget)) {
      // Mesh sem jawOpen/mouthFunnel/mouthPucker: nada a dirigir — o analyser
      // ficou com o legado (driverActive=false), esta linha só explica.
      console.warn("[MirrorLiveAvatar] mesh " + String(lipTarget.name ?? "?") + " sem os morphs da boca — analyser segue ao legado do AvatarCore");
      return;
    }
    const driver = new LiveMouthDriver(lipTarget, analyserNode);
    driverRef.current = driver;
    driver.start();
    return () => {
      driver.stop(); // zera os três: nunca deixa a boca aberta ao trocar de mesh/analyser
      driverRef.current = null;
    };
  }, [lipTarget, analyserNode]);
  useEffect(() => {
    driverRef.current?.setSpeaking(energyState === "speaking");
  }, [energyState, lipTarget, analyserNode]);
  // O halo pulsa com o MESMO estado da energia — no Realtime não há
  // audioElement para escutar, e isSpeaking ficaria falso para sempre.
  const haloSpeaking = energyState === "speaking";
  // Fonte de áudio da energia por estado: mic no listening (Realtime), voz
  // remota no speaking (Realtime) ou o <audio> do TTS (pipeline antigo).
  const energySource: MediaStream | HTMLAudioElement | null =
    energyState === "listening"
      ? micStream ?? null
      : energyState === "speaking"
        ? remoteStream ?? audioElement ?? null
        : null;

  // isAvaturnModel is the LEGACY calibration GATE in AvatarCore — true selects
  // the "calibration-type-A" path that actually APPLIES scaleOverride /
  // cameraDistanceOverride / lookAtYOverride (true bust). false routes to the
  // MetaPerson auto-fit path that ignores them and frames torso/jeans with the
  // face cropped. So for a bust it MUST be true regardless of provider — exactly
  // what AvatarCard/AvatarTeacher and miniMirrorCalibration (MINI_MIRROR_IS_AVATURN_MODEL)
  // do. Not per-provider.

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center"
    >
      {/* Presence halo — recreated (not imported from frozen MirrorAvatar). */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: 520,
          height: 520,
          background:
            "radial-gradient(circle, hsl(187 72% 50% / 0.16), transparent 68%)",
        }}
        animate={{
          opacity: haloSpeaking ? [0.7, 1, 0.7] : [0.45, 0.65, 0.45],
          scale: haloSpeaking ? [1, 1.06, 1] : [1, 1.03, 1],
        }}
        transition={{ duration: haloSpeaking ? 2.4 : 4.5, ease: "easeInOut", repeat: Infinity }}
      />
      {/* Inner depth shadow — grounds the avatar. */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[42%] w-[420px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 55% at 50% 45%, hsl(210 50% 10% / 0.8), transparent 75%)",
        }}
      />

      {/* Avatar — bust framing + the SAME lip-sync wiring as the Mirror. */}
      <div className="relative z-10 w-full h-full pointer-events-auto">
        {/* Anti-flash: espera os DOIS loadings (avatar E mirror_surface) —
            pintar antes da preferência resolver mostra a superfície errada e
            troca na frente do usuário (classe do P0 #3). */}
        {!loading && !surfaceLoading &&
          (showAvatar ? (
            <AvatarViewer
              modelUrl={avatarUrl || "/avatar/model_small.glb"}
              height="100%"
              enableRotate={false}
              framing="bust"
              isAvaturnModel={true}
              scaleOverride={MIRROR_LIVE_CONFIG.scale}
              cameraDistanceOverride={MIRROR_LIVE_CONFIG.cameraDistance}
              lookAtYOverride={MIRROR_LIVE_CONFIG.lookAtY}
              audioElement={audioElement}
              alignment={alignment}
              analyserNode={driverActive ? null : analyserNode ?? null}
              onLipSyncTargetReady={onLipSyncTargetReady}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {/* Energia no lugar do portal bust (task c7406112). Diâmetro
                  espelha o círculo do portal bust (~64% do lado menor);
                  teto 400px preserva a lens automática do wrapper. */}
              <div style={{ width: "min(64vw, 46vh, 400px)", aspectRatio: "1 / 1" }}>
                <VyvEnergy
                  state={energyState}
                  level={energyState === "listening" ? micLevel : undefined}
                  source={energySource}
                />
              </div>
            </div>
          ))}
      </div>
    </motion.div>
  );
}
